import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logInfo, logError } from '@/lib/productionLogger';

export type ConnectionStatus = 'pending' | 'accepted' | 'incoming';

interface ConnectionRequest {
  id: string;
  requester_id: string;
  requested_id: string;
  status: 'pending' | 'accepted' | 'declined';
  message?: string;
  created_at: string;
}

export const useConnectionStatus = (currentUserId?: string) => {
  const [statusMap, setStatusMap] = useState<Map<string, ConnectionStatus>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [connectionRequests, setConnectionRequests] = useState<ConnectionRequest[]>([]);

  // Fonction pour mettre à jour le statut d'une connexion
  const updateStatus = useCallback((request: ConnectionRequest, currentUserId: string) => {
    const otherUserId = request.requester_id === currentUserId ? request.requested_id : request.requester_id;
    
    let status: ConnectionStatus;
    if (request.status === 'accepted') {
      status = 'accepted';
    } else if (request.requester_id === currentUserId) {
      status = 'pending'; // Demande envoyée
    } else {
      status = 'incoming'; // Demande reçue
    }
    
    setStatusMap(prev => new Map(prev).set(otherUserId, status));
  }, []);

  // Fonction pour supprimer un statut
  const removeStatus = useCallback((request: ConnectionRequest, currentUserId: string) => {
    const otherUserId = request.requester_id === currentUserId ? request.requested_id : request.requester_id;
    setStatusMap(prev => {
      const newMap = new Map(prev);
      newMap.delete(otherUserId);
      return newMap;
    });
  }, []);

  // Chargement initial des connection requests
  const loadConnectionRequests = useCallback(async () => {
    if (!currentUserId) {
      setIsLoading(false);
      return;
    }

    try {
      logInfo('Loading connection requests for user', { userId: currentUserId });
      
      const { data, error } = await supabase
        .from('connection_requests')
        .select('*')
        .or(`requester_id.eq.${currentUserId},requested_id.eq.${currentUserId}`)
        .neq('status', 'declined'); // Ignorer les demandes refusées

      if (error) {
        logError('Error loading connection requests', error);
        return;
      }

      if (data) {
        logInfo('Connection requests loaded', { count: data.length });
        setConnectionRequests(data as ConnectionRequest[]);
        
        // Construire la map de statuts
        const newStatusMap = new Map<string, ConnectionStatus>();
        data.forEach((request) => {
          const otherUserId = request.requester_id === currentUserId ? request.requested_id : request.requester_id;
          
          if (request.status === 'accepted') {
            newStatusMap.set(otherUserId, 'accepted');
          } else if (request.requester_id === currentUserId) {
            newStatusMap.set(otherUserId, 'pending');
          } else {
            newStatusMap.set(otherUserId, 'incoming');
          }
        });
        
        setStatusMap(newStatusMap);
      }
    } catch (err) {
      logError('Exception loading connection requests', err);
    } finally {
      setIsLoading(false);
    }
  }, [currentUserId]);

  // 🔥 PHASE 1: Remplacé Realtime par polling (30s)
  useEffect(() => {
    if (!currentUserId) return;

    loadConnectionRequests();

    logInfo('Setting up polling for connection_requests (30s interval)');

    import('@/utils/realtimeOptimizer').then(({ createPollingInterval }) => {
      const stopPolling = createPollingInterval(
        'connection_requests',
        () => loadConnectionRequests(),
        true
      );

      return () => {
        logInfo('Cleaning up polling');
        stopPolling();
      };
    });
  }, [currentUserId, loadConnectionRequests]);

  // Fonction pour obtenir le statut d'une connexion
  const getConnectionStatus = useCallback((targetUserId: string): ConnectionStatus | null => {
    return statusMap.get(targetUserId) ?? null;
  }, [statusMap]);

  // Fonction pour envoyer une demande de connexion
  const sendRequest = useCallback(async (targetUserId: string, message?: string): Promise<boolean> => {
    if (!currentUserId) {
      logError('Cannot send request: no current user');
      return false;
    }

    try {
      logInfo('Sending connection request', { targetUserId, hasMessage: !!message });

      // ✅ MISE À JOUR OPTIMISTE : Mettre à jour l'UI immédiatement
      setStatusMap(prev => new Map(prev).set(targetUserId, 'pending'));

      const { data, error } = await supabase
        .from('connection_requests')
        .insert({
          requester_id: currentUserId,
          requested_id: targetUserId,
          status: 'pending',
          message: message || null
        })
        .select('id')
        .single();

      if (error) {
        logError('Error sending connection request', error);
        // ❌ Si erreur, annuler la mise à jour optimiste
        setStatusMap(prev => {
          const newMap = new Map(prev);
          newMap.delete(targetUserId);
          return newMap;
        });
        return false;
      }

      logInfo('Connection request sent successfully');
      
      // 📧 Envoyer email de notification (non-bloquant)
      supabase.functions.invoke('email-connection-request', {
        body: {
          requesterId: currentUserId,
          requestedId: targetUserId,
          requestId: data.id,
          message: message
        }
      }).then(res => {
        if (res.error) {
          logError('Failed to send connection request email', res.error);
        } else {
          logInfo('Connection request email sent');
        }
      }).catch(err => {
        logError('Email function error', err);
      });
      
      return true;
    } catch (err) {
      logError('Exception sending connection request', err);
      // ❌ Si erreur, annuler la mise à jour optimiste
      setStatusMap(prev => {
        const newMap = new Map(prev);
        newMap.delete(targetUserId);
        return newMap;
      });
      return false;
    }
  }, [currentUserId]);

  // Fonction pour répondre à une demande de connexion
  const respondToRequest = useCallback(async (requestId: string, accept: boolean): Promise<boolean> => {
    try {
      logInfo('Responding to connection request', { requestId, accept });

      // Récupérer les infos de la demande avant de la mettre à jour
      const { data: request, error: fetchError } = await supabase
        .from('connection_requests')
        .select('requester_id, requested_id')
        .eq('id', requestId)
        .single();

      if (fetchError) {
        logError('Error fetching connection request', fetchError);
        return false;
      }

      const { error } = await supabase
        .from('connection_requests')
        .update({ status: accept ? 'accepted' : 'declined' })
        .eq('id', requestId);

      if (error) {
        logError('Error responding to connection request', error);
        return false;
      }

      logInfo('Response sent successfully');
      
      // 📧 Si accepté, envoyer email de notification (non-bloquant)
      if (accept && request && currentUserId) {
        supabase.functions.invoke('email-connection-accepted', {
          body: {
            accepterId: currentUserId,
            requesterId: request.requester_id,
            requestId: requestId
          }
        }).then(res => {
          if (res.error) {
            logError('Failed to send connection accepted email', res.error);
          } else {
            logInfo('Connection accepted email sent');
          }
        }).catch(err => {
          logError('Email function error', err);
        });
      }
      
      return true;
    } catch (err) {
      logError('Exception responding to connection request', err);
      return false;
    }
  }, [currentUserId]);

  // Fonction pour obtenir toutes les demandes de connexion reçues
  const getIncomingRequests = useCallback(() => {
    return connectionRequests.filter(req => 
      req.requested_id === currentUserId && req.status === 'pending'
    );
  }, [connectionRequests, currentUserId]);

  // Fonction pour obtenir toutes les demandes envoyées en attente
  const getPendingRequests = useCallback(() => {
    return connectionRequests.filter(req => 
      req.requester_id === currentUserId && req.status === 'pending'
    );
  }, [connectionRequests, currentUserId]);

  // Fonction pour obtenir toutes les connexions acceptées
  const getAcceptedConnections = useCallback(() => {
    return connectionRequests.filter(req => req.status === 'accepted');
  }, [connectionRequests]);

  return {
    getConnectionStatus,
    sendRequest,
    respondToRequest,
    getIncomingRequests,
    getPendingRequests,
    getAcceptedConnections,
    isLoading
  };
};
